/*
 * @Author: Antes
 * @Email: tangzhlyd@gmail.com
 * @Date: 2021/12/14 14:23
 * @Description: 用于发送请求，会自动在请求headers里面追加 routeCode
 */
import { head, mergeLeft, mergeRight, not } from "ramda";
import { useMount, useUpdate } from "@/hooks";
import { isArray, isObject } from "@/utils";
import { useCallback, useRef } from "react";
import { handleParam } from "@/hooks/useTable";
import { useRoute } from "@/hooks/useRoute";

const defaultOptions = {
  defaultLoading: false,
  manual: false,
  abort: false,
};
type Service<TData, TParams extends any[]> = (...args: TParams) => Promise<TData>;

interface Options<R, P extends any[]> {
  manual?: boolean; // 加载 是否自动发送
  onSuccess?: (data: R, params: P) => void; // 成功回调
  onError?: (e: string, params: P) => void; // 失败回调
  defaultLoading?: boolean; // 默认 loading 状态
  defaultParams?: P;
  defaultPagination?: {
    pageSize: number;
    current: number;
  };
  pending?: boolean; // 是否等待上次请求结束 才发送请求 如果为true 上次请求没有结束 中间执行多次run 都不会发起请求
  noLoading?: boolean;
  defaultOrders?: { asc: boolean; column: string }[];
  abort?: boolean;
}
interface OptionsTrue<R, P extends any[]> extends Options<R, P> {
  abort: true;
}
interface OptionsFalse<R, P extends any[]> extends Options<R, P> {
  abort?: false;
}
interface State<T, P> {
  loading: boolean;
  code?: string;
  data?: T;
  params: P;
}
export interface Result<TData, TParams extends any[]> {
  code?: string;
  data?: TData;
  refresh: () => void;
  run: (...param: TParams) => Promise<TData>;
  loading: boolean;
  params: TParams | [];
}
interface Abort {
  abort: () => void;
}

export default function useRequest<TData, TParams extends any[]>(
  service: Service<TData, TParams>,
  options?: OptionsTrue<TData, TParams>,
): Result<TData, TParams> & Abort;

export default function useRequest<TData, TParams extends any[]>(
  service: Service<TData, TParams>,
  options?: OptionsFalse<TData, TParams>,
): Result<TData, TParams>;

export default function useRequest<TData, TParams extends any[]>(
  service: Service<TData, TParams>,
  options: Options<TData, TParams> = {
    defaultLoading: false,
    manual: false,
    abort: false,
  },
) {
  const { code } = useRoute();
  const update = useUpdate();
  const thisOptions = useRef(options);
  thisOptions.current = mergeLeft(options, defaultOptions);
  const controllerRef = useRef<AbortController>();
  const state = useRef<State<TData, TParams>>({
    loading: false,
    params: (thisOptions.current.defaultParams ?? []) as TParams,
  });
  state.current.code = code;
  const serviceRef = useRef(service);
  serviceRef.current = service;
  const run = useCallback(
    (param: TParams): Promise<TData> => {
      if (thisOptions.current.pending && state.current.loading) {
        return new Promise(() => {
          console.log(thisOptions.current.pending);
        });
      }
      if (thisOptions.current.abort) {
        controllerRef.current = new AbortController();
      }
      const thisParam = handleParam(
        param,
        { presetsLen: serviceRef.current.length, actualLen: param.length },
        {
          headers: {
            routeCode: state.current.code,
          },
        },
      );
      state.current.loading = true;
      if (not(thisOptions.current.noLoading)) {
        update();
      }
      return new Promise((resolve) => {
        serviceRef
          .current(...thisParam)
          .then((item) => {
            controllerRef.current = undefined;
            if (thisOptions.current.onSuccess) {
              thisOptions.current.onSuccess(item, param);
            }
            state.current.loading = false;
            state.current.data = item;
            state.current.params = param as any;
            update();
            resolve(item);
          })
          .catch((err: string) => {
            controllerRef.current = undefined;
            if (thisOptions.current.onError) {
              thisOptions.current.onError(err, param);
            }
            state.current.loading = false;
            state.current.data = undefined;
            state.current.params = param as any;
            update();
          });
      });
    },
    [update],
  );
  const refresh = useCallback(() => {
    run(state.current.params);
  }, [run]);
  // 首次 判断是否默认执行
  useMount(() => {
    if (!thisOptions.current.manual) {
      const data = (thisOptions.current.defaultParams ?? []) as TParams;
      if (data && isArray(data)) {
        const res = head(data);
        if (isObject(res)) {
          data[0] = mergeRight(head(data), { orders: thisOptions.current.defaultOrders });
        }
      }
      run(data);
    }
  });
  const _run = useCallback(
    (...params: TParams) => {
      return run(params);
    },
    [run],
  );
  const abort = useCallback(() => {
    controllerRef.current?.abort();
  }, []);
  if (thisOptions.current.abort) {
    return { run: _run, refresh, ...state.current, abort };
  }
  return { run: _run, refresh, ...state.current };
}
